import { apiFetch } from "@/lib/apiClient";

/**
 * Wraps the /api/images endpoints. Uploads return an opaque image id (e.g.
 * "abc-def-123.jpg") that should be stored as `imageFrontRef` / `imageBackRef`
 * on a Card record. The URL for display is `/api/images/{id}`.
 *
 * Failures bubble as thrown Errors so callers can show inline error UI
 * instead of having to inspect a status field.
 */

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // mirrors backend limit
export const ACCEPTED_IMAGE_MIME = "image/jpeg,image/png,image/webp,image/gif";

interface UploadResponse {
  id: string;
  url: string;
}

export async function uploadImage(file: File): Promise<string> {
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(
      `Image is ${(file.size / 1024 / 1024).toFixed(1)} MB — must be under 10 MB.`,
    );
  }
  const fd = new FormData();
  fd.append("image", file);
  const res = await apiFetch("/api/images", { method: "POST", body: fd });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Upload failed (${res.status})`);
  }
  const data = (await res.json()) as UploadResponse;
  return data.id;
}

/**
 * Server-side fetch + persist of an external image URL. Mirrors uploadImage
 * but for the "we already know where the image lives" path used by the CSV
 * importers and bulk-add — see features/cardImageBackfill.ts.
 *
 * The server enforces an allowlist (currently `images.pokemontcg.io` +
 * `images.carduploader.com`) so this helper will reject other hosts with a
 * 403 — caller should treat that as "image not available" and move on.
 */
export async function fetchImageFromUrl(url: string): Promise<string> {
  const res = await apiFetch("/api/images/from-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Image fetch failed (${res.status})`);
  }
  const data = (await res.json()) as UploadResponse;
  return data.id;
}

/**
 * Rotate a card photo by 90 / 180 / 270 degrees clockwise (TNK-102). Fetches
 * the current image, draws it rotated to a canvas, uploads the result as a
 * new /api/images blob, deletes the old one (best-effort), and returns the
 * new image id. PNG / WebP rotations are lossless; JPEG re-encode runs at
 * quality 0.92. GIF inputs are re-encoded as a single frame.
 *
 * Throws on fetch / decode / upload failure so the caller can surface inline.
 */
const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
};

export async function rotateImage(
  currentRef: string,
  degrees: 90 | 180 | 270,
): Promise<string> {
  const url = imageUrl(currentRef);
  if (!url) throw new Error("No image to rotate");

  const res = await apiFetch(url);
  if (!res.ok) {
    throw new Error(`Couldn't load image (${res.status})`);
  }
  const sourceBlob = await res.blob();
  // Some browsers return blob.type === "" for cached responses; fall back to
  // the extension on the ref. The server-side MIME validation depends on the
  // upload's reported type, so we have to pick a real one.
  const mime =
    sourceBlob.type ||
    Object.entries(MIME_TO_EXT).find(([, ext]) =>
      currentRef.toLowerCase().endsWith(`.${ext}`),
    )?.[0] ||
    "image/jpeg";

  const bitmap = await createImageBitmap(sourceBlob);
  const swap = degrees === 90 || degrees === 270;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? bitmap.height : bitmap.width;
  canvas.height = swap ? bitmap.width : bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    throw new Error("Canvas 2D context unavailable");
  }
  // Pivot around the canvas center so the rotated bitmap lands inside the
  // (potentially swapped) bounding box without clipping.
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  bitmap.close();

  const outBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) =>
        b
          ? resolve(b)
          : reject(new Error("Canvas export failed — image too large?")),
      mime,
      mime === "image/jpeg" ? 0.92 : undefined,
    );
  });

  const ext = MIME_TO_EXT[mime] ?? "jpg";
  const file = new File([outBlob], `rotated.${ext}`, { type: mime });
  const newId = await uploadImage(file);
  void deleteImage(currentRef);
  return newId;
}

/**
 * Best-effort delete — silently no-ops on 404 or network error so callers
 * can fire this when removing a card without blocking the delete flow.
 */
export async function deleteImage(id: string): Promise<void> {
  if (!id) return;
  try {
    await apiFetch(`/api/images/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  } catch {
    /* silent */
  }
}

/**
 * TNK-125 image variant sizes. The server backs each upload with three
 * files on disk:
 *
 *   - `thumb`    — 200×280 WebP (~10 KB). Card-grid thumbnails.
 *   - `medium`   — 480×672 WebP (~50 KB). Card detail + modal art panel.
 *   - `original` — uploaded format, untouched. Lightbox + scan/OCR.
 *
 * Missing variants fall through to the original server-side, so a
 * `?size=` request is never a hard failure — useful when interacting
 * with images uploaded before the variant pipeline shipped (the
 * backfill script handles the long tail).
 */
export type ImageSize = "thumb" | "medium" | "original";

export function imageUrl(
  id: string | undefined,
  size: ImageSize = "original",
): string | null {
  if (!id) return null;
  const base = `/api/images/${encodeURIComponent(id)}`;
  return size === "original" ? base : `${base}?size=${size}`;
}
